/**
 * Premium "Digital Egg" intro scene.
 *
 * Story-driven cinematic egg. Two operating modes:
 *
 *  1) Single-beat variants (legacy): `inputs.variant ∈ { "appear", "glow",
 *     "cracks", "break" }`. Used when the editor wants to stage a single
 *     phase in isolation (e.g. for storyboard / inspector).
 *
 *  2) Full story mode (default for new reels): `inputs.variant = "story"`
 *     (or "full" / "all"). The scene plays the COMPLETE Allyver-origin
 *     sequence in one long clip (8–12 s):
 *
 *        appear → glow → cracks → BREAK → mockup pop-in
 *
 *     A new caption fades in for each beat, so the viewer reads the
 *     origin story while the egg evolves. Captions are configurable via
 *     `inputs.items` as `[ "EYEBROW | Headline", ... ]`. Defaults follow
 *     the official Allyver "Über uns" narrative ("Alles begann mit einem
 *     Ei.").
 *
 * The egg itself is a CSS `border-radius`-tweaked oval with a polished
 * radial gradient + glassmorphism rim. Slow rotation is faked with a
 * wobble on `rotateY` and a sweeping highlight band. Cracks are an SVG
 * draw-in. The shatter spawns shards, particles and a connecting network
 * of nodes. Finally, an enlarged phone mockup (carrying the user's
 * media) materialises from the centre as the "answer" to the origin
 * story.
 *
 * Backdrop is forced to `solid` so the black void stays uninterrupted.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor,
  clipOutroFade,
  combineSceneFragments,
  escapeHtml,
  gsapSetLine,
  makeTl,
  typeSize,
  weights,
  withAlpha,
  colors as themeColors,
} from "../../reel/sceneShared.js";
import { phoneFrame, tabletFrame, laptopFrame, screenContent } from "../visual/_mockHelpers.js";

type Phase = "appear" | "glow" | "cracks" | "break" | "story";
type MockupDevice = "none" | "mobile" | "tablet" | "laptop";

function parsePhase(v: string | undefined): Phase {
  const s = (v ?? "story").toLowerCase();
  if (s === "story" || s === "full" || s === "all") return "story";
  if (s === "glow" || s === "cracks" || s === "break") return s;
  if (s === "appear") return "appear";
  // New default: full origin-story sequence.
  return "story";
}

function parseMockupDevice(v: unknown): MockupDevice {
  const s = String(v ?? "").toLowerCase();
  if (s === "mobile" || s === "phone" || s === "smartphone") return "mobile";
  if (s === "tablet" || s === "ipad") return "tablet";
  if (s === "laptop" || s === "macbook" || s === "desktop") return "laptop";
  return "none";
}

// Allyver Über-uns Story-Captions (Eyebrow | Headline). Werden verwendet
// wenn der Nutzer keine eigenen `items` setzt.
const DEFAULT_STORY_CAPTIONS: ReadonlyArray<{ eye: string; title: string }> = [
  { eye: "DIE GESCHICHTE",  title: "Alles begann mit einem Ei." },
  { eye: "EINE FRAGE",      title: "Warum bleiben lokale Anbieter unsichtbar?" },
  { eye: "DIE ERKENNTNIS",  title: "Nicht nur Eier. Sieben ganze Branchen." },
  { eye: "ALLYVER",         title: "Alles vereint. In einer App." },
] as const;

function parseStoryCaptions(input: unknown): Array<{ eye: string; title: string }> {
  // Accept `items: ["EYEBROW | Headline", ...]`. Missing slots fall back
  // to the default narrative so the scene never goes empty.
  const out: Array<{ eye: string; title: string }> = [];
  const raw = Array.isArray(input) ? input : [];
  for (let i = 0; i < 4; i++) {
    const def = DEFAULT_STORY_CAPTIONS[i]!;
    const item = raw[i];
    if (typeof item === "string" && item.trim()) {
      const [a, b] = item.split("|").map((s) => s.trim());
      out.push({ eye: (b ? a : "") || def.eye, title: (b ?? a) || def.title });
    } else {
      out.push({ eye: def.eye, title: def.title });
    }
  }
  return out;
}

export const digitalEgg: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const phase = parsePhase(inputs.variant);
  const isStory = phase === "story";
  const isVertical = ctx.format === "9:16";
  const text = inputs.text ?? "";
  const sub = inputs.subtext ?? "";

  // In story mode the mockup is part of the climax → render it whenever
  // a device is specified, defaulting to a phone (since the website
  // ends on "Eine App").
  const mockupDevice: MockupDevice = isStory
    ? (parseMockupDevice(inputs.extras?.mockupDevice) === "none" ? "mobile" : parseMockupDevice(inputs.extras?.mockupDevice))
    : parseMockupDevice(inputs.extras?.mockupDevice);
  const showMockup = (phase === "break" && mockupDevice !== "none") || (isStory && mockupDevice !== "none");

  // Egg slightly smaller in story mode to leave room for the caption AND
  // for the bigger mockup that takes over at the end. In legacy mode we
  // keep the original sizing.
  const eggH = isStory
    ? (isVertical ? 640 : 620)
    : (isVertical ? 760 : 720);
  const eggW = Math.round(eggH * 0.76);

  // Caption sizes are smaller in story mode (we have 4 of them and need
  // them not to fight the visuals).
  const titleSize = isStory
    ? typeSize(isVertical ? "md" : "lg", ctx.format) * 0.92
    : typeSize(isVertical ? "lg" : "xl", ctx.format) * 0.78;
  const subSize = isStory
    ? typeSize("sm", ctx.format) * 0.85
    : typeSize("md", ctx.format) * 0.55;

  const t = makeTl(clipStartSec);

  const wrapId = `${clipPrefix}-wrap`;
  const stageId = `${clipPrefix}-stage`;
  const eggBoxId = `${clipPrefix}-eggbox`;
  const eggId = `${clipPrefix}-egg`;
  const sheenId = `${clipPrefix}-sheen`;
  const rimId = `${clipPrefix}-rim`;
  const reflectionId = `${clipPrefix}-refl`;
  const haloId = `${clipPrefix}-halo`;
  const coreId = `${clipPrefix}-core`;
  const ring1Id = `${clipPrefix}-ring1`;
  const ring2Id = `${clipPrefix}-ring2`;
  const ring3Id = `${clipPrefix}-ring3`;
  const cracksId = `${clipPrefix}-cracks`;
  const shardsId = `${clipPrefix}-shards`;
  const particlesId = `${clipPrefix}-particles`;
  const networkId = `${clipPrefix}-network`;
  const titleId = `${clipPrefix}-title`;
  const subId = `${clipPrefix}-sub`;
  const dotsId = `${clipPrefix}-dots`;
  const mockupWrapId = `${clipPrefix}-mockwrap`;
  const mockupDeviceId = `${clipPrefix}-mockdev`;
  const mockupGlowId = `${clipPrefix}-mockglow`;
  const captionStackId = `${clipPrefix}-capstack`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "solid", animated: false });

  // ----- Egg body
  const eggGradient = `radial-gradient(120% 130% at 38% 28%, #ffffff 0%, #f8f9fb 38%, #d4d8e0 70%, #6e7280 96%, #2a2d36 100%)`;
  const eggShadow = `inset -40px -80px 100px 8px rgba(0,0,0,0.55), inset 30px 30px 60px 0 rgba(255,255,255,0.65), 0 50px 120px rgba(0,0,0,0.7), 0 0 60px ${withAlpha(accent, 0.08)}`;

  // ----- Cracks paths
  const crackPaths = [
    "M50 22 L48 32 L54 38 L50 48 L56 56 L52 64 L58 72",
    "M50 22 L45 28 L42 36 L46 44 L40 52 L44 60",
    "M50 22 L55 30 L62 36 L60 46 L66 54 L62 64",
    "M50 22 L48 14 L52 8",
    "M50 32 L40 38 L36 48 L32 56",
    "M50 32 L60 38 L66 46 L72 54",
  ];
  const crackLen = 320;

  // ----- Shards (break phase)
  const shardCount = 12;
  const shards = Array.from({ length: shardCount }, (_, i) => {
    const angle = (i / shardCount) * Math.PI * 2 + Math.PI / shardCount;
    const radius = eggW * 0.42;
    const cx = 50 + Math.cos(angle) * 32;
    const cy = 50 + Math.sin(angle) * 38;
    const w = 14 + (i % 3) * 6;
    const h = 18 + ((i + 1) % 3) * 5;
    const rot = (angle * 180) / Math.PI;
    const fill = i % 4 === 0 ? "#fff" : i % 4 === 1 ? "#eef0f4" : i % 4 === 2 ? "#cfd3dc" : "#a1a6b4";
    const id = `${clipPrefix}-shd-${i}`;
    return {
      id,
      html: `<div id="${id}" data-angle="${angle.toFixed(4)}" data-radius="${radius.toFixed(2)}" style="position:absolute;left:${cx}%;top:${cy}%;width:${w}px;height:${h}px;transform:translate(-50%,-50%) rotate(${rot.toFixed(2)}deg);background:${fill};clip-path:polygon(50% 0%, 100% 35%, 85% 100%, 12% 88%, 0% 30%);box-shadow:0 8px 20px rgba(0,0,0,0.55), inset -3px -8px 14px rgba(0,0,0,0.35), inset 3px 3px 6px rgba(255,255,255,0.6);opacity:0;"></div>`,
    };
  });

  // ----- Particles (break phase)
  const particleCount = 56;
  const particleSeed = (i: number): { x: number; y: number; s: number; hue: string; d: number } => {
    const a = (i * 137.508) % 360;
    const r = 50 + ((i * 53) % 220);
    const ang = (a * Math.PI) / 180;
    const x = Math.cos(ang) * r;
    const y = Math.sin(ang) * r;
    const s = 3 + ((i * 7) % 9);
    const hue = i % 5 === 0 ? accent : i % 5 === 1 ? "#a855f7" : i % 5 === 2 ? "#22d3c5" : "#ffffff";
    const d = 320 + ((i * 31) % 360);
    return { x, y, s, hue, d };
  };
  const particles = Array.from({ length: particleCount }, (_, i) => {
    const p = particleSeed(i);
    const id = `${clipPrefix}-p-${i}`;
    return {
      id,
      target: p,
      html: `<div id="${id}" style="position:absolute;left:50%;top:50%;width:${p.s}px;height:${p.s}px;border-radius:9999px;background:${p.hue};box-shadow:0 0 ${p.s * 4}px ${p.hue};opacity:0;transform:translate(-50%,-50%);"></div>`,
    };
  });

  // ----- Network nodes/edges (break phase)
  const nodeCount = 7;
  const nodes = Array.from({ length: nodeCount }, (_, i) => {
    const ang = (i / nodeCount) * Math.PI * 2 - Math.PI / 2;
    const r = 0.42;
    const cx = 0.5 + Math.cos(ang) * r;
    const cy = 0.5 + Math.sin(ang) * r * (isVertical ? 0.95 : 0.75);
    return { id: `${clipPrefix}-n-${i}`, cx, cy };
  });
  const edges: string[] = [];
  for (let i = 0; i < nodeCount; i++) {
    for (let j = i + 1; j < nodeCount; j++) {
      if (Math.abs(i - j) > 3 && Math.abs(i - j) < nodeCount - 3) continue;
      const a = nodes[i]!;
      const b = nodes[j]!;
      const id = `${clipPrefix}-e-${i}-${j}`;
      edges.push(
        `<line id="${id}" x1="${a.cx * 100}%" y1="${a.cy * 100}%" x2="${b.cx * 100}%" y2="${b.cy * 100}%" stroke="${withAlpha(themeColors.fg, 0.45)}" stroke-width="1.1" stroke-dasharray="6 4" />`,
      );
    }
  }
  const networkSvg = `<svg id="${networkId}" style="position:absolute;inset:0;width:100%;height:100%;overflow:visible;pointer-events:none;opacity:0;">
    <defs>
      <radialGradient id="${clipPrefix}-nodeGrad" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="${themeColors.fg}" />
        <stop offset="60%" stop-color="${withAlpha(themeColors.fg, 0.4)}" />
        <stop offset="100%" stop-color="${withAlpha(themeColors.fg, 0)}" />
      </radialGradient>
    </defs>
    ${edges.join("\n")}
    ${nodes
      .map(
        (n, i) => `<g id="${n.id}">
        <circle cx="${n.cx * 100}%" cy="${n.cy * 100}%" r="22" fill="url(#${clipPrefix}-nodeGrad)" />
        <circle cx="${n.cx * 100}%" cy="${n.cy * 100}%" r="6" fill="${i % 3 === 0 ? accent : i % 3 === 1 ? "#a855f7" : "#22d3c5"}" />
      </g>`,
      )
      .join("\n")}
  </svg>`;

  // ----- Concentric rings
  const ring = (id: string, sizeFactor: number, dash: string, color: string, opacity: number): string =>
    `<div id="${id}" style="position:absolute;left:50%;top:50%;width:${(eggW * sizeFactor).toFixed(0)}px;height:${(eggH * sizeFactor).toFixed(0)}px;margin-left:${(-(eggW * sizeFactor) / 2).toFixed(0)}px;margin-top:${(-(eggH * sizeFactor) / 2).toFixed(0)}px;border-radius:9999px;border:1.5px ${dash} ${withAlpha(color, opacity)};box-shadow:0 0 30px ${withAlpha(color, 0.15)};opacity:0;pointer-events:none;"></div>`;

  // ----- Cracks SVG
  const cracksSvg = `<svg id="${cracksId}" viewBox="0 0 100 100" preserveAspectRatio="none" style="position:absolute;inset:0;width:100%;height:100%;overflow:visible;pointer-events:none;mix-blend-mode:screen;opacity:0;">
    <defs>
      <filter id="${clipPrefix}-crackGlow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="0.6" />
      </filter>
    </defs>
    <g filter="url(#${clipPrefix}-crackGlow)">
      ${crackPaths
        .map(
          (d, i) =>
            `<path id="${clipPrefix}-ck-${i}" d="${d}" fill="none" stroke="#fff7c2" stroke-width="0.55" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="${crackLen}" stroke-dashoffset="${crackLen}" />`,
        )
        .join("\n")}
    </g>
  </svg>`;

  // ----- Ambient floor dust
  const dustHtml = `<div id="${dotsId}" style="position:absolute;inset:0;background-image:radial-gradient(${withAlpha(themeColors.fg, 0.06)} 1px, transparent 1px);background-size:48px 48px;opacity:0.18;pointer-events:none;"></div>`;

  // ----- Mockup (story mode → bigger phone; legacy → original size)
  //
  // Wichtig: In STORY-Mode wird das Mockup NICHT mehr in die Egg-Stage
  // gerendert (wo es bei moderner iPhone-Aspect-Ratio die Stage-Bounds
  // sprengt und in die Caption darunter ragt). Stattdessen bauen wir eine
  // eigene "Mockup-Beat-Group" — Climax-Caption oben, Phone darunter,
  // beides als Block im Viewport zentriert. Diese Group fadet beim Pop-In
  // ein, während die alte Egg+Caption-Achse ausfadet.
  // LEGACY-Mode (phase="break" einzeln) behält das alte Verhalten:
  // Mockup absolut in der Egg-Stage.
  let mockupHtml = "";
  let mockupGroupHtml = "";
  let frameHtmlForGroup = "";
  if (showMockup) {
    const fallback = mockupDevice === "laptop" ? "feed" : mockupDevice === "tablet" ? "list" : "app";
    const inner = screenContent({ media: inputs.media, accent, fallback });

    let frameHtml = "";
    if (mockupDevice === "mobile") {
      // Story-climax: kompakteres Phone, damit Caption + Phone gemeinsam
      // sauber im Viewport zentriert sitzen (mit moderner iPhone-Aspect-
      // Ratio ist die Phone-Höhe ~2.09× die Breite — bei 420 px Breite
      // also ~877 px Höhe, plus Caption ~200 px + Gap ~60 px ≈ 1140 px,
      // passt in 1920 mit Padding).
      const phoneW = isStory
        ? (isVertical ? 420 : 380)
        : (isVertical ? 360 : 320);
      frameHtml = phoneFrame({ id: mockupDeviceId, width: phoneW, accent, inner });
    } else if (mockupDevice === "tablet") {
      const tabletW = isStory ? (isVertical ? 540 : 660) : (isVertical ? 540 : 620);
      const tabletH = Math.round(tabletW * (isVertical ? 1.36 : 0.75));
      frameHtml = tabletFrame({ id: mockupDeviceId, width: tabletW, height: tabletH, accent, inner });
    } else {
      const laptopW = isStory ? (isVertical ? 700 : 820) : (isVertical ? 640 : 760);
      frameHtml = laptopFrame({ id: mockupDeviceId, width: laptopW, accent, inner });
    }
    frameHtmlForGroup = frameHtml;

    if (!isStory) {
      // Legacy: Mockup absolut in der Egg-Stage (alter Pfad).
      mockupHtml = `<div id="${mockupWrapId}" style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);z-index:20;pointer-events:none;opacity:0;">
        <div id="${mockupGlowId}" style="position:absolute;left:50%;top:50%;width:160%;height:140%;transform:translate(-50%,-50%);background:radial-gradient(closest-side, ${withAlpha(accent, 0.55)} 0%, ${withAlpha("#a855f7", 0.22)} 38%, transparent 72%);filter:blur(40px);opacity:0;z-index:-1;"></div>
        ${frameHtml}
      </div>`;
    }
  }

  // ----- Egg stage
  const eggStageHtml = `<div id="${stageId}" style="position:relative;width:${eggW}px;height:${eggH}px;perspective:1600px;perspective-origin:50% 40%;">
    <div id="${haloId}" style="position:absolute;left:50%;top:50%;width:${(eggW * 1.9).toFixed(0)}px;height:${(eggH * 1.5).toFixed(0)}px;margin-left:${(-(eggW * 1.9) / 2).toFixed(0)}px;margin-top:${(-(eggH * 1.5) / 2).toFixed(0)}px;border-radius:50%;background:radial-gradient(closest-side, ${withAlpha(themeColors.fg, 0.18)} 0%, ${withAlpha(accent, 0.05)} 35%, transparent 70%);filter:blur(28px);opacity:0;pointer-events:none;"></div>

    ${ring(ring1Id, 1.18, "dashed", "#22d3c5", 0.45)}
    ${ring(ring2Id, 1.42, "dotted", "#a855f7", 0.4)}
    ${ring(ring3Id, 1.7, "solid",  "#ffffff", 0.18)}

    <div id="${eggBoxId}" style="position:absolute;left:0;top:0;width:100%;height:100%;transform-style:preserve-3d;will-change:transform;">
      <div id="${eggId}" style="position:absolute;inset:0;border-radius:50% 50% 50% 50% / 56% 56% 44% 44%;background:${eggGradient};background-size:160% 160%;background-position:45% 35%;box-shadow:${eggShadow};transform-style:preserve-3d;">
        <div id="${rimId}" style="position:absolute;inset:0;border-radius:inherit;background:radial-gradient(closest-side at 50% 50%, transparent 60%, ${withAlpha(accent, 0.35)} 92%, transparent 100%);mix-blend-mode:screen;opacity:0.55;pointer-events:none;"></div>
        <div id="${sheenId}" style="position:absolute;left:20%;top:8%;width:48%;height:30%;border-radius:50%;background:radial-gradient(closest-side, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.15) 60%, transparent 100%);filter:blur(6px);transform:rotate(-18deg);pointer-events:none;"></div>
        <div id="${coreId}" style="position:absolute;left:50%;top:42%;width:34%;height:30%;margin-left:-17%;margin-top:-15%;border-radius:50%;background:radial-gradient(closest-side, ${withAlpha("#fff7c2", 0.95)} 0%, ${withAlpha(accent, 0.55)} 35%, transparent 80%);filter:blur(12px);opacity:0;pointer-events:none;mix-blend-mode:screen;"></div>
        ${cracksSvg}
      </div>
    </div>

    <div id="${reflectionId}" style="position:absolute;left:50%;top:100%;width:${(eggW * 0.95).toFixed(0)}px;height:${(eggH * 0.18).toFixed(0)}px;margin-left:${(-(eggW * 0.95) / 2).toFixed(0)}px;margin-top:6px;background:radial-gradient(50% 100% at 50% 0%, ${withAlpha(themeColors.fg, 0.18)} 0%, transparent 70%);filter:blur(6px);opacity:0;pointer-events:none;"></div>

    <div id="${shardsId}" style="position:absolute;inset:0;pointer-events:none;">${shards.map((s) => s.html).join("")}</div>
    <div id="${particlesId}" style="position:absolute;left:50%;top:50%;width:0;height:0;pointer-events:none;">${particles.map((p) => p.html).join("")}</div>
    ${networkSvg}
    ${mockupHtml}
  </div>`;

  // ----- Captions
  // Story mode renders 4 stacked captions (only one visible at a time);
  // legacy mode keeps the single text/subtext pair.
  let captionsHtml = "";
  let storyCaps: Array<{ eye: string; title: string }> = [];
  const storyClimaxInGroup = isStory && showMockup;
  if (isStory) {
    storyCaps = parseStoryCaptions(inputs.items);
    const blocks = storyCaps.map((c, i) => {
      // Die Climax-Caption (Index 3) wird im Story+Mockup-Modus in die
      // separate Mockup-Beat-Group ausgelagert (oberhalb des Phones,
      // beides als Pair zentriert). Hier auslassen, damit es keine
      // doppelten DOM-IDs gibt.
      if (storyClimaxInGroup && i === 3) return "";
      const eyeHtml = c.eye
        ? `<div id="${clipPrefix}-cap-eye-${i}" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.7)};text-align:center;">${escapeHtml(c.eye)}</div>`
        : "";
      const titleHtml2 = c.title
        ? `<div id="${clipPrefix}-cap-title-${i}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titleSize}px;line-height:1.1;letter-spacing:-1.2px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 880 : 1300}px;text-shadow:0 0 60px ${withAlpha(themeColors.fg, 0.35)};">${escapeHtml(c.title)}</div>`
        : "";
      return `<div id="${clipPrefix}-cap-${i}" style="position:absolute;left:0;right:0;top:0;display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 22 : 18}px;opacity:0;">${eyeHtml}${titleHtml2}</div>`;
    });
    // Reserve enough vertical space for the tallest caption (title +
    // eyebrow + gap). Approximation good enough to keep the layout from
    // collapsing as we crossfade absolute-positioned blocks.
    const capHeight = Math.round(titleSize * 2.6 + subSize * 1.8 + 36);
    captionsHtml = `<div id="${captionStackId}" style="position:relative;width:100%;height:${capHeight}px;">${blocks.join("")}</div>`;

    // Mockup-Beat-Group: Climax-Caption (Index 3) DIREKT ÜBER dem Phone,
    // beides als Block zentriert. `opacity:0` initial; wird beim Pop-In
    // gemeinsam mit dem Mockup eingeblendet (siehe Tween-Block unten).
    if (storyClimaxInGroup) {
      const climax = storyCaps[3]!;
      const climaxEyeHtml = climax.eye
        ? `<div id="${clipPrefix}-cap-eye-3" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.7)};text-align:center;">${escapeHtml(climax.eye)}</div>`
        : "";
      const climaxTitleHtml = climax.title
        ? `<div id="${clipPrefix}-cap-title-3" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titleSize}px;line-height:1.1;letter-spacing:-1.2px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 880 : 1300}px;text-shadow:0 0 60px ${withAlpha(themeColors.fg, 0.35)};">${escapeHtml(climax.title)}</div>`
        : "";
      mockupGroupHtml = `<div id="${mockupWrapId}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 48 : 36}px;padding:${isVertical ? "120px 64px" : "64px 96px"};z-index:25;pointer-events:none;opacity:0;">
  <div id="${clipPrefix}-cap-3" style="display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 22 : 18}px;">
    ${climaxEyeHtml}
    ${climaxTitleHtml}
  </div>
  <div style="position:relative;">
    <div id="${mockupGlowId}" style="position:absolute;left:50%;top:50%;width:160%;height:140%;transform:translate(-50%,-50%);background:radial-gradient(closest-side, ${withAlpha(accent, 0.55)} 0%, ${withAlpha("#a855f7", 0.22)} 38%, transparent 72%);filter:blur(40px);opacity:0;z-index:-1;"></div>
    ${frameHtmlForGroup}
  </div>
</div>`;
    }
  } else {
    const titleHtml = text
      ? `<div id="${titleId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titleSize}px;line-height:1.1;letter-spacing:-1.2px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 880 : 1300}px;text-shadow:0 0 60px ${withAlpha(themeColors.fg, 0.35)};">${escapeHtml(text)}</div>`
      : "";
    const subHtml = sub
      ? `<div id="${subId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.7)};text-align:center;">${escapeHtml(sub)}</div>`
      : "";
    const captionGap = isVertical ? 48 : 36;
    captionsHtml = titleHtml || subHtml
      ? `<div style="display:flex;flex-direction:column;align-items:center;gap:${captionGap}px;">${subHtml}${titleHtml}</div>`
      : "";
  }

  // Story mode: das Mockup ist deutlich höher als die Egg-Stage; der Gap
  // muss groß genug sein, damit das untere Drittel des Phones nicht in
  // die Caption-Zeile hineinläuft.
  const stageGap = isVertical
    ? (isStory ? 200 : 96)
    : (isStory ? 140 : 70);

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;background:#000;">
  ${dustHtml}
  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${stageGap}px;padding:${isVertical ? "120px 64px" : "64px 96px"};">
    ${eggStageHtml}
    ${captionsHtml}
  </div>
  ${mockupGroupHtml}
</div>`;

  // ----- Initial state -----
  // Story mode → start from the "appear" precondition (egg invisible
  // and small). Legacy → existing per-phase logic.
  const eggInitOpacity = (phase === "appear" || isStory) ? 0 : 1;
  const eggInitScale = (phase === "appear" || isStory) ? 0.86 : 1;
  const haloInitOpacity = (phase === "appear" || isStory) ? 0 : 0.7;
  const reflectionInitOpacity = (phase === "appear" || isStory) ? 0 : 0.6;
  const ring1InitOpacity = phase === "glow" || phase === "cracks" ? 1 : 0;
  const ring2InitOpacity = phase === "glow" || phase === "cracks" ? 0.7 : 0;
  const ring3InitOpacity = phase === "glow" || phase === "cracks" ? 0.5 : 0;
  const coreInitOpacity = phase === "cracks" ? 0.4 : 0;
  const cracksInitOpacity = phase === "cracks" ? 1 : 0;

  const initParts: string[] = [
    gsapSetLine(`#${eggBoxId}`, { opacity: eggInitOpacity, scale: eggInitScale, rotationY: -4 }),
    gsapSetLine(`#${haloId}`, { opacity: haloInitOpacity, scale: (phase === "appear" || isStory) ? 0.8 : 1 }),
    gsapSetLine(`#${reflectionId}`, { opacity: reflectionInitOpacity }),
    gsapSetLine(`#${ring1Id}`, { opacity: ring1InitOpacity, rotation: 0 }),
    gsapSetLine(`#${ring2Id}`, { opacity: ring2InitOpacity, rotation: 0 }),
    gsapSetLine(`#${ring3Id}`, { opacity: ring3InitOpacity, rotation: 0 }),
    gsapSetLine(`#${coreId}`, { opacity: coreInitOpacity, scale: 0.8 }),
    gsapSetLine(`#${cracksId}`, { opacity: cracksInitOpacity }),
    gsapSetLine(`#${shardsId}`, { opacity: phase === "break" ? 1 : 0 }),
    gsapSetLine(`#${networkId}`, { opacity: 0, scale: 0.92 }),
  ];

  if (phase === "cracks" || phase === "break") {
    crackPaths.forEach((_, i) => {
      initParts.push(gsapSetLine(`#${clipPrefix}-ck-${i}`, { attr: { "stroke-dashoffset": 0 } }));
    });
  }

  if (isStory) {
    // All captions hidden, slightly offset for entry.
    for (let i = 0; i < 4; i++) {
      initParts.push(gsapSetLine(`#${clipPrefix}-cap-${i}`, { opacity: 0, y: 16 }));
    }
  } else {
    if (text) initParts.push(gsapSetLine(`#${titleId}`, { opacity: 0, y: 18 }));
    if (sub) initParts.push(gsapSetLine(`#${subId}`, { opacity: 0, y: 12 }));
  }

  const tweens: string[] = [];

  // ----- Slow rotation / sheen drift (all variants).
  tweens.push(t.to(`#${eggBoxId}`, { rotationY: 4, duration: 6.5, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 0));
  tweens.push(t.to(`#${eggId}`,    { backgroundPosition: "55% 35%", duration: 6.5, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 0));

  // ============================================================
  // STORY MODE — full sequence in one clip
  // ============================================================
  if (isStory) {
    const D = clipDurationSec;
    // Beat anchors as fractions of the clip duration — scale gracefully
    // for any 8–12s clip. Generous overlaps avoid empty space.
    const appearAt = 0.15;
    const glowAt   = Math.max(2.0, D * 0.24);
    const crackAt  = Math.max(4.0, D * 0.46);
    const breakAt  = Math.max(5.8, D * 0.66);
    const popAt    = Math.max(6.8, D * 0.76);

    // --- APPEAR
    tweens.push(t.to(`#${eggBoxId}`,     { opacity: 1, scale: 1,  duration: 1.4, ease: "raw:\"power3.out\"" }, appearAt));
    tweens.push(t.to(`#${haloId}`,       { opacity: 0.7, scale: 1, duration: 1.6, ease: "raw:\"power2.out\"" }, appearAt + 0.1));
    tweens.push(t.to(`#${reflectionId}`, { opacity: 0.6, duration: 1.4, ease: "raw:\"power2.out\"" }, appearAt + 0.3));

    // --- GLOW
    tweens.push(t.to(`#${ring1Id}`, { opacity: 0.7, duration: 0.9, ease: "raw:\"power2.out\"" }, glowAt));
    tweens.push(t.to(`#${ring2Id}`, { opacity: 0.5, duration: 0.9, ease: "raw:\"power2.out\"" }, glowAt + 0.15));
    tweens.push(t.to(`#${ring3Id}`, { opacity: 0.3, duration: 0.9, ease: "raw:\"power2.out\"" }, glowAt + 0.3));
    tweens.push(t.to(`#${ring1Id}`, { rotation: 360, duration: 12, repeat: -1, ease: "raw:\"none\"" }, glowAt));
    tweens.push(t.to(`#${ring2Id}`, { rotation: -360, duration: 18, repeat: -1, ease: "raw:\"none\"" }, glowAt));
    tweens.push(t.to(`#${ring3Id}`, { rotation: 360, duration: 24, repeat: -1, ease: "raw:\"none\"" }, glowAt));
    tweens.push(t.to(`#${haloId}`,  { opacity: 0.95, scale: 1.05, duration: 1.4, yoyo: true, repeat: -1, ease: "raw:\"sine.inOut\"" }, glowAt + 0.2));

    // --- CRACKS
    crackPaths.forEach((_, i) => {
      const id = `#${clipPrefix}-ck-${i}`;
      tweens.push(t.set(id, { attr: { "stroke-dashoffset": crackLen } }, 0));
      tweens.push(t.to(id, { attr: { "stroke-dashoffset": 0 }, duration: 0.55, ease: "raw:\"power2.out\"" }, crackAt + i * 0.16));
    });
    tweens.push(t.fromTo(`#${cracksId}`, { opacity: 0 }, { opacity: 1, duration: 0.3 }, crackAt));
    tweens.push(t.to(`#${coreId}`, { opacity: 0.85, scale: 1.05, duration: 1.2, ease: "raw:\"power2.out\"" }, crackAt + 0.2));
    tweens.push(t.to(`#${coreId}`, { scale: 1.18, opacity: 1, duration: 0.9, yoyo: true, repeat: -1, ease: "raw:\"sine.inOut\"" }, crackAt + 1.2));

    // --- BREAK
    tweens.push(t.to(`#${coreId}`, { scale: 1.8, opacity: 1, duration: 0.45, ease: "raw:\"power2.in\"" }, breakAt - 0.2));
    tweens.push(t.to(`#${haloId}`, { opacity: 1, scale: 1.45, duration: 0.7, ease: "raw:\"power2.out\"" }, breakAt - 0.2));
    tweens.push(t.to(`#${eggId}`,    { opacity: 0, duration: 0.35, ease: "raw:\"power2.out\"" }, breakAt));
    tweens.push(t.to(`#${cracksId}`, { opacity: 0, duration: 0.35, ease: "raw:\"power2.out\"" }, breakAt));
    tweens.push(t.to(`#${rimId}`,    { opacity: 0, duration: 0.3 }, breakAt));
    tweens.push(t.to(`#${sheenId}`,  { opacity: 0, duration: 0.3 }, breakAt));
    tweens.push(t.to(`#${reflectionId}`, { opacity: 0, duration: 0.3 }, breakAt));
    tweens.push(t.set(`#${shardsId}`, { opacity: 1 }, breakAt - 0.05));
    shards.forEach((s, i) => {
      const ang = parseFloat(s.html.match(/data-angle="([^"]+)"/)?.[1] ?? "0");
      const dx = Math.cos(ang) * (380 + (i % 4) * 40);
      const dy = Math.sin(ang) * (420 + (i % 4) * 40);
      const rot = ((i * 73) % 360) - 180;
      tweens.push(t.to(`#${s.id}`, { opacity: 1, duration: 0.08 }, breakAt));
      tweens.push(t.to(`#${s.id}`, { x: dx, y: dy, rotation: rot, opacity: 0, duration: 1.4, ease: "raw:\"power3.out\"" }, breakAt + 0.02));
    });
    tweens.push(t.to(`#${coreId}`, { scale: 2.4, opacity: 1, duration: 0.25, ease: "raw:\"power3.out\"" }, breakAt));
    tweens.push(t.to(`#${coreId}`, { scale: 0.6, opacity: 0, duration: 0.6, ease: "raw:\"power2.in\"" }, breakAt + 0.35));
    particles.forEach((p) => {
      tweens.push(t.set(`#${p.id}`, { x: 0, y: 0, opacity: 0, scale: 0.4 }, 0));
      tweens.push(t.to(`#${p.id}`, { opacity: 1, scale: 1, duration: 0.18, ease: "raw:\"power2.out\"" }, breakAt + 0.05));
      tweens.push(t.to(`#${p.id}`, { x: p.target.x * 3.2, y: p.target.y * 3.2, opacity: 0, scale: 0.4, duration: 1.6, ease: "raw:\"power3.out\"" }, breakAt + 0.08));
    });
    const netTargetOpacity = showMockup ? 0.45 : 1;
    const netTargetScale = showMockup ? 0.85 : 1;
    tweens.push(t.to(`#${networkId}`, { opacity: netTargetOpacity, scale: netTargetScale, duration: 0.9, ease: "raw:\"power3.out\"" }, breakAt + 0.55));
    nodes.forEach((n, i) => {
      tweens.push(t.fromTo(`#${n.id}`, { scale: 0.5, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.55, ease: "raw:\"back.out(1.7)\"", transformOrigin: "center" }, breakAt + 0.55 + i * 0.07));
    });

    // --- MOCKUP POP-IN
    if (showMockup) {
      tweens.push(
        t.fromTo(
          `#${mockupWrapId}`,
          { opacity: 0, scale: 0.35, y: 30, rotationX: -8 },
          { opacity: 1, scale: 1, y: 0, rotationX: 0, duration: 0.9, ease: "raw:\"back.out(1.6)\"" },
          popAt,
        ),
      );
      tweens.push(t.to(`#${mockupGlowId}`, { opacity: 1, duration: 0.7, ease: "raw:\"power2.out\"" }, popAt + 0.1));
      const idleDur = Math.max(0.8, D - (popAt + 0.9) - 0.4);
      tweens.push(t.to(`#${mockupWrapId}`, { y: -10, duration: idleDur, yoyo: true, repeat: -1, ease: "raw:\"sine.inOut\"" }, popAt + 1.0));
      // Network gently fades a bit further once the mockup has taken
      // over, so the device is the unambiguous hero.
      tweens.push(t.to(`#${networkId}`, { opacity: 0.18, duration: 0.9, ease: "raw:\"power2.out\"" }, popAt + 0.4));
    }

    // --- STORY CAPTIONS (crossfaded, one per beat)
    // Cap windows are anchored to the same beat times, but the LAST
    // caption (the climax) stays visible until the very end.
    const capStarts = [appearAt + 0.3, glowAt + 0.15, crackAt + 0.2, popAt - 0.05];
    const capEnds   = [glowAt + 0.05, crackAt + 0.1, breakAt - 0.05, Math.max(D - 0.4, popAt + 1.0)];
    for (let i = 0; i < 4; i++) {
      const start = capStarts[i]!;
      const end = capEnds[i]!;
      tweens.push(t.to(`#${clipPrefix}-cap-${i}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, start));
      // Climax caption (i === 3) doesn't fade until the outro takes over.
      if (i < 3) {
        tweens.push(t.to(`#${clipPrefix}-cap-${i}`, { opacity: 0, y: -10, duration: 0.45, ease: "raw:\"power2.in\"" }, end));
      }
    }

    tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 12));
    return combineSceneFragments([backdrop, { html, init: initParts.filter(Boolean).join("\n"), tweens }]);
  }

  // ============================================================
  // LEGACY single-phase modes (appear / glow / cracks / break)
  // ============================================================
  if (phase === "appear") {
    tweens.push(t.to(`#${eggBoxId}`, { opacity: 1, scale: 1, duration: 1.3, ease: "raw:\"power3.out\"" }, 0.15));
    tweens.push(t.to(`#${haloId}`, { opacity: 0.7, scale: 1, duration: 1.6, ease: "raw:\"power2.out\"" }, 0.25));
    tweens.push(t.to(`#${reflectionId}`, { opacity: 0.6, duration: 1.4, ease: "raw:\"power2.out\"" }, 0.5));
  }

  if (phase === "glow" || phase === "cracks") {
    if (phase === "glow") {
      tweens.push(t.to(`#${ring1Id}`, { opacity: 0.7, scale: 1, duration: 1.0, ease: "raw:\"power2.out\"" }, 0.1));
      tweens.push(t.to(`#${ring2Id}`, { opacity: 0.5, scale: 1, duration: 1.0, ease: "raw:\"power2.out\"" }, 0.3));
      tweens.push(t.to(`#${ring3Id}`, { opacity: 0.3, scale: 1, duration: 1.0, ease: "raw:\"power2.out\"" }, 0.5));
    }
    tweens.push(t.to(`#${ring1Id}`, { rotation: 360, duration: 12, repeat: -1, ease: "raw:\"none\"" }, 0));
    tweens.push(t.to(`#${ring2Id}`, { rotation: -360, duration: 18, repeat: -1, ease: "raw:\"none\"" }, 0));
    tweens.push(t.to(`#${ring3Id}`, { rotation: 360, duration: 24, repeat: -1, ease: "raw:\"none\"" }, 0));
    tweens.push(t.to(`#${haloId}`, { opacity: 0.95, scale: 1.05, duration: 1.4, yoyo: true, repeat: -1, ease: "raw:\"sine.inOut\"" }, 0.2));
  }

  if (phase === "cracks") {
    crackPaths.forEach((_, i) => {
      const id = `#${clipPrefix}-ck-${i}`;
      tweens.push(t.set(id, { attr: { "stroke-dashoffset": crackLen } }, 0));
      tweens.push(t.to(id, { attr: { "stroke-dashoffset": 0 }, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.15 + i * 0.18));
    });
    tweens.push(t.fromTo(`#${cracksId}`, { opacity: 0 }, { opacity: 1, duration: 0.3 }, 0.05));
    tweens.push(t.to(`#${coreId}`, { opacity: 0.85, scale: 1.05, duration: 1.6, ease: "raw:\"power2.out\"" }, 0.6));
    tweens.push(t.to(`#${coreId}`, { scale: 1.18, opacity: 1, duration: 1.0, yoyo: true, repeat: -1, ease: "raw:\"sine.inOut\"" }, 1.2));
  }

  if (phase === "break") {
    tweens.push(t.set(`#${cracksId}`, { opacity: 1 }, 0));
    tweens.push(t.set(`#${coreId}`, { opacity: 0.95, scale: 1.1 }, 0));
    tweens.push(t.set(`#${ring1Id}`, { opacity: 0.6 }, 0));
    tweens.push(t.set(`#${ring2Id}`, { opacity: 0.4 }, 0));
    tweens.push(t.set(`#${ring3Id}`, { opacity: 0.25 }, 0));
    tweens.push(t.to(`#${ring1Id}`, { rotation: 360, duration: 12, repeat: -1, ease: "raw:\"none\"" }, 0));
    tweens.push(t.to(`#${ring2Id}`, { rotation: -360, duration: 18, repeat: -1, ease: "raw:\"none\"" }, 0));

    const breakAt = 0.55;
    tweens.push(t.to(`#${coreId}`, { scale: 1.6, opacity: 1, duration: breakAt, ease: "raw:\"power2.in\"" }, 0));
    tweens.push(t.to(`#${haloId}`, { opacity: 1, scale: 1.4, duration: breakAt + 0.2, ease: "raw:\"power2.out\"" }, 0));
    tweens.push(t.to(`#${eggId}`, { opacity: 0, duration: 0.35, ease: "raw:\"power2.out\"" }, breakAt));
    tweens.push(t.to(`#${cracksId}`, { opacity: 0, duration: 0.35, ease: "raw:\"power2.out\"" }, breakAt));
    tweens.push(t.to(`#${rimId}`, { opacity: 0, duration: 0.3 }, breakAt));
    tweens.push(t.to(`#${sheenId}`, { opacity: 0, duration: 0.3 }, breakAt));
    tweens.push(t.to(`#${reflectionId}`, { opacity: 0, duration: 0.3 }, breakAt));

    shards.forEach((s, i) => {
      const ang = parseFloat(s.html.match(/data-angle="([^"]+)"/)?.[1] ?? "0");
      const dx = Math.cos(ang) * (380 + (i % 4) * 40);
      const dy = Math.sin(ang) * (420 + (i % 4) * 40);
      const rot = ((i * 73) % 360) - 180;
      tweens.push(t.to(`#${s.id}`, { opacity: 1, duration: 0.08 }, breakAt));
      tweens.push(t.to(`#${s.id}`, { x: dx, y: dy, rotation: rot, opacity: 0, duration: 1.4, ease: "raw:\"power3.out\"" }, breakAt + 0.02));
    });

    tweens.push(t.to(`#${coreId}`, { scale: 2.4, opacity: 1, duration: 0.25, ease: "raw:\"power3.out\"" }, breakAt));
    tweens.push(t.to(`#${coreId}`, { scale: 0.6, opacity: 0, duration: 0.6, ease: "raw:\"power2.in\"" }, breakAt + 0.35));

    particles.forEach((p) => {
      tweens.push(t.set(`#${p.id}`, { x: 0, y: 0, opacity: 0, scale: 0.4 }, 0));
      tweens.push(t.to(`#${p.id}`, { opacity: 1, scale: 1, duration: 0.18, ease: "raw:\"power2.out\"" }, breakAt + 0.05));
      tweens.push(t.to(`#${p.id}`, { x: p.target.x * 3.2, y: p.target.y * 3.2, opacity: 0, scale: 0.4, duration: 1.6, ease: "raw:\"power3.out\"" }, breakAt + 0.08));
    });

    const netTargetOpacity = showMockup ? 0.55 : 1;
    const netTargetScale = showMockup ? 0.85 : 1;
    tweens.push(t.to(`#${networkId}`, { opacity: netTargetOpacity, scale: netTargetScale, duration: 0.9, ease: "raw:\"power3.out\"" }, breakAt + 0.55));
    nodes.forEach((n, i) => {
      tweens.push(t.fromTo(`#${n.id}`, { scale: 0.5, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.55, ease: "raw:\"back.out(1.7)\"", transformOrigin: "center" }, breakAt + 0.55 + i * 0.07));
    });

    if (showMockup) {
      const popAt = breakAt + 1.1;
      tweens.push(
        t.fromTo(
          `#${mockupWrapId}`,
          { opacity: 0, scale: 0.35, y: 30, rotationX: -8 },
          { opacity: 1, scale: 1, y: 0, rotationX: 0, duration: 0.85, ease: "raw:\"back.out(1.6)\"" },
          popAt,
        ),
      );
      tweens.push(t.to(`#${mockupGlowId}`, { opacity: 1, duration: 0.7, ease: "raw:\"power2.out\"" }, popAt + 0.1));
      const idleDur = Math.max(0.8, clipDurationSec - (popAt + 0.85) - 0.4);
      tweens.push(t.to(`#${mockupWrapId}`, { y: -8, duration: idleDur, yoyo: true, repeat: -1, ease: "raw:\"sine.inOut\"" }, popAt + 0.95));
    }
  }

  const captionAt = phase === "appear" ? 1.05 : phase === "break" ? 0.95 : 0.45;
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, captionAt));
  if (text) tweens.push(t.to(`#${titleId}`, { opacity: 1, y: 0, duration: 0.7, ease: "raw:\"power3.out\"" }, captionAt + 0.15));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 12));

  return combineSceneFragments([backdrop, { html, init: initParts.filter(Boolean).join("\n"), tweens }]);
};
